<script>
import moment from 'moment';
import { Scatter } from 'vue-chartjs';

export default {
  extends: Scatter,
  props: {
    data: {
      type: Object,
      default: null,
    },
    isCurrency: {
      type: Boolean,
      default: true,
    },
    itemValueDecorator: {
      type: Function,
      default: null,
    },
    multiplier: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      datasets: [],
      xAxes: [],
      currentDatasetConfig: {},
      compareDatasetConfig: {},
      currentXAxisConfig: {},
      compareXAxisConfig: {},
      currentLineColor: '#743B91',
      compareLineColor: '#51A5D3',
    };
  },
  watch: {
    data: function () {
      this.renderLineChart();
    },
  },
  created() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const currentGradient = ctx.createLinearGradient(0, 0, 0, 300);
    currentGradient.addColorStop(0, this.currentLineColor);
    currentGradient.addColorStop(1, '#fff');

    const compareGradient = ctx.createLinearGradient(0, 50, 0, 280);
    compareGradient.addColorStop(0, this.compareLineColor);
    compareGradient.addColorStop(1, '#fff');

    this.currentDatasetConfig = {
      label: 'Current',
      xAxisID: 'x-axis-1',
      borderColor: this.currentLineColor,
      backgroundColor: currentGradient,
      pointHoverBackgroundColor: '#fff',
      showLine: true,
      borderWidth: 2,
      lineTension: 0.2,
      spanGaps: false,
    };
    this.compareDatasetConfig = {
      label: 'Compared',
      xAxisID: 'x-axis-2',
      borderColor: this.compareLineColor,
      backgroundColor: compareGradient,
      pointHoverBackgroundColor: '#fff',
      showLine: true,
      borderWidth: 2,
      lineTension: 0.2,
      spanGaps: false,
    };
    this.currentXAxisConfig = {
      id: 'x-axis-1',
      type: 'category',
      ticks: {
        display: false,
      },
      position: 'bottom',
      gridLines: {
        display: false,
      },
    };
    this.compareXAxisConfig = {
      display: false,
      id: 'x-axis-2',
      type: 'category',
      ticks: {
        display: false,
      },
      gridLines: {
        display: false,
      },
    };
  },
  methods: {
    equalizeArrays(arr1, arr2) {
      const maxLength = Math.max(arr1.length, arr2.length);

      while (arr1.length < maxLength) {
        arr1.push({ date: '', value: null });
      }

      while (arr2.length < maxLength) {
        arr2.push({ date: '', value: null });
      }

      return [arr1, arr2];
    },
    renderLineChart: function () {
      this.datasets = [];
      this.xAxes = [];

      const { current, compare } = this.data || {};

      const getLabels = (arr) => arr.map((el) => el.date);
      const getRangeForTooltip = (arr) => arr.map((el) => el.range);
      const getData = (arr) => arr.map((el) => {
        if (this.itemValueDecorator) {
          return this.itemValueDecorator(el.value, this.multiplier);
        }

        return el.value;
      });
      const formatDate = (date, format = 'L') => (date ? moment(date).format(format) : null);
      const getXValue = (xAxisID, tooltipItem) => {
        let xValue;

        if (
          this.xAxes[xAxisID].dateRange[tooltipItem.index].start !==
          this.xAxes[xAxisID].dateRange[tooltipItem.index].end
        ) {
          xValue = `${this.formatDate(
            this.xAxes[xAxisID].dateRange[tooltipItem.index].start,
            'L',
          )} - ${this.formatDate(this.xAxes[xAxisID].dateRange[tooltipItem.index].end, 'L')}`;
        } else {
          xValue = this.formatDate(this.xAxes[xAxisID].labels[tooltipItem.index], 'L');
        }

        return xValue;
      };

      const updateAxisAndDataset = (data, datasetConfig, axisConfig) => {
        return {
          dataset: { data: getData(data), ...datasetConfig },
          axis: { labels: getLabels(data), dateRange: getRangeForTooltip(data), ...axisConfig },
        };
      };

      if (current) {
        if (compare) {
          [this.data.current, this.data.compare] = this.equalizeArrays(current, compare);
          const compareData = updateAxisAndDataset(
            compare,
            this.compareDatasetConfig,
            this.compareXAxisConfig,
          );
          this.datasets.push(compareData.dataset);
          this.xAxes.push(compareData.axis);
        }

        const currentData = updateAxisAndDataset(
          current,
          this.currentDatasetConfig,
          this.currentXAxisConfig,
        );
        this.datasets.push(currentData.dataset);
        this.xAxes.push(currentData.axis);
      }

      const xLabels = function () {
        const ctx = this.chart.ctx;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';

        const drawLabels = (xAxis, color, yOffset) => {
          if (!xAxis) return;
          const firstTick =
            xAxis.options.dateRange && xAxis.options.dateRange[0]
              ? formatDate(xAxis.options.dateRange[0].start, 'L')
              : '';
          const lastTick =
            xAxis.options.dateRange && xAxis.options.dateRange[xAxis.ticks.length - 1]
              ? formatDate(xAxis.options.dateRange[xAxis.ticks.length - 1].end, 'L')
              : '';
          ctx.fillStyle = color;
          if (firstTick || lastTick) {
            ctx.fillText(firstTick, xAxis.left + 30, xAxis.bottom + yOffset);
            ctx.fillText(lastTick, xAxis.right - 30, xAxis.bottom + yOffset);
          }
        };

        drawLabels(this.scales['x-axis-1'], '#743B91', 7);
        drawLabels(this.scales['x-axis-2'], '#51A5D3', 30);
      };

      this.renderChart(
        {
          datasets: this.datasets,
        },
        {
          tooltips: {
            mode: 'index',
            callbacks: {
              label: function (tooltipItem, data) {
                var xAxisID = tooltipItem.datasetIndex;
                if (!this.xAxes[xAxisID] || !this.xAxes[xAxisID].labels) return '';
                var xValue = getXValue(xAxisID, tooltipItem);
                var yValue = data.datasets[xAxisID].data[tooltipItem.index];
                return `${xValue}: ${
                  this.isCurrency
                    ? `$${this.formatAmount(yValue)}`
                    : yValue.toLocaleString()
                }`;
              }.bind(this),
            },
          },
          legend: {
            display: true,
            position: 'bottom',
            reverse: true,
            labels: {
              boxWidth: 10,
              padding: 30,
              usePointStyle: true,
            },
          },
          layout: {
            padding: {
              bottom: 50,
              bottom: 0,
            },
          },
          scales: {
            xAxes: this.xAxes,
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                  min: 0,
                  callback: (value) => {
                    return this.isCurrency ? `${this.formatAmount(value)}` : value;
                  },
                },
              },
            ],
          },
          plugins: [
            {
              afterDraw: xLabels,
            },
          ],
          animation: {
            onProgress: xLabels,
          },
          elements: {
            point: {
              radius: 0,
              hitRadius: 10,
              hoverRadius: 4,
              hoverBorderWidth: 3,
            },
          },
        },
      );
    },
  },
  mounted() {
    this.renderLineChart();
  },
};
</script>
